import {useMemo} from 'react';

type Props = {
  course: LearningPathCourse;
  currentSection: SectionIndexItem;
};
const COURSE_IMAGES_BUCKET = process.env.NEXT_PUBLIC_COURSE_IMAGES_BUCKET;
export const LearningPathThumbnail = ({course, currentSection}: Props) => {
  const thumbnailImage = useMemo(() => {
    if (course) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(currentSection.content, 'text/html');
      const iframe = doc.querySelector('iframe');

      if (iframe && iframe.src.indexOf('youtube') !== -1) {
        return `https://img.youtube.com/vi/${iframe.src.split('/').pop()}/0.jpg`;
      }

      const img = doc.querySelector('img');
      if (img && img.src) {
        return img.src;
      }

      if (course.img_url) return `${COURSE_IMAGES_BUCKET}/learning_paths/${course.img_url}`;

      if (course.learning_path_img)
        return `${COURSE_IMAGES_BUCKET}/learning_paths/${course.learning_path_img}`;

      return getFallbackImageForTopicOfInterest(course, currentSection);
    }
  }, [course, currentSection]);

  return <img width={280} height={160} src={thumbnailImage} alt={course?.title} />;
};

const getFallbackImageForTopicOfInterest = (
  course: LearningPathCourse,
  currentSection: SectionIndexItem,
) => {
  switch (course.topic_of_interest) {
    case 'sql':
      return getSignedImage(COURSE_IMAGE_FALLBACK.sql, currentSection.uuid);
    case 'product metrics':
      return getSignedImage(COURSE_IMAGE_FALLBACK.product, currentSection.uuid);
    case 'ml system design':
      return getSignedImage(COURSE_IMAGE_FALLBACK.ml, currentSection.uuid);
    case 'statistics':
    case 'probability':
      return getSignedImage(COURSE_IMAGE_FALLBACK.statistics, currentSection.uuid);
    case 'python':
      return getSignedImage(COURSE_IMAGE_FALLBACK.code, currentSection.uuid);
    default:
      return '/img/logo.svg';
  }
};

const getSignedImage = (array: string[], currentLessonUUID: string) => {
  const indexForCourse = getSignature(currentLessonUUID, array.length);
  return `${process.env.NEXT_PUBLIC_CMS_IMAGES_BUCKET}/${array[indexForCourse]}`;
};

// Minimalistic hash function that makes that the same uuid, always gets the same image
const getSignature = (str: string, N: number) => {
  let hash = 0,
    i,
    chr;
  if (str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return Math.abs(hash) % N;
};

const COURSE_IMAGE_FALLBACK = {
  code: [
    'b8767352-b373-48fc-b73d-bd846f482a87.webp',
    '50328ed4-45c6-458b-aff7-b5753c000dc8.webp',
    'cd58de06-422b-41d1-862d-d3907d6b3e5f.webp',
    'ffe2dc1e-45d0-4c2a-add6-9fa97fb8f791.webp',
    '8379cf8e-5db7-483b-bc85-bc45f31888f7.webp',
    '5503e341-9635-4b6f-8110-fbd87351cd1a.webp',
    '82b8fc8c-d75a-43cd-b1c5-8d8f834a2c88.webp',
    'c3383453-d808-458c-9b42-6e6f119e661c.webp',
    'f10d585c-51a1-4eda-836e-42362a24ab96.webp',
    'b030e3cf-fdb6-46f2-aada-2209743ab669.webp',
    'c07c5aad-0d7c-40bb-b7a8-6ef4d4b982c0.webp',
    '5a8997da-4793-41ab-a5d8-3e08e86238fa.webp',
    '42c3d769-27c0-455c-9ec7-3a72ef8c64bd.webp',
    'ab9f1864-b42b-4651-86a9-14124dc536e4.webp',
    '120b4dc0-d549-4ee2-bd39-ebc836bed5ea.webp',
  ],
  ml: [
    'dc624ad8-431d-45ac-9818-7abc742e3517.webp',
    'dd06dbe6-d578-409e-821d-1dafcc5d7610.webp',
    '2d6132e2-317d-467f-82ec-e33b0ae5bf9c.webp',
    'c5d3581c-410e-460b-b721-a4fb7e614e2b.webp',
    '32d822d4-cc2a-4884-b70b-956429f1805a.webp',
    '885d868a-b61a-4002-a38d-c9a93450cdea.webp',
    'f612b3a7-9f7b-4b3d-bbc2-56de311a357b.webp',
    '730b8669-98cf-4008-a9a5-63c749628d80.webp',
    'b9b3d3e9-80e3-4e2c-9440-da065a2dd1ed.webp',
    '91825929-82b4-447e-94a9-f550ca3521b6.webp',
    'ee9525d3-3613-459e-9849-d2999bf0242e.webp',
    '6302be93-7a4c-4336-a774-618f11e7f32d.webp',
    '5eb9e85b-59af-4ff0-84e0-4f0526fdf431.webp',
    'e4597bd3-9abd-4f0a-895a-f4cc9dee57e1.webp',
    '6e3c34e6-9962-4733-a8da-5a72af8b65ad.webp',
    '123a8199-11cd-4bbd-977e-376f393a43d9.webp',
    '9fea5c4c-6d7d-49ca-9528-c5e4b1f393bb.webp',
    'f6a9e678-04d0-4e2e-9910-64eca14d9233.webp',
    'c2d1b2d0-537e-4830-93cc-4843018bd5cd.webp',
    'a4be65ed-2844-45c7-85e4-d25e123f2ff4.webp',
    'd82656e1-8038-45fd-b75b-c0ebeaded009.webp',
  ],
  product: [
    'ed3e3b7c-a52a-4864-b72a-dfc4d04218fa.webp',
    '78615276-7174-4914-b343-823ac018e16c.webp',
    '565f4d04-f70d-486c-b91b-22337dc3308e.webp',
    '503af8e0-3b21-43b3-9cc4-593325e400ff.webp',
    'b30aa93b-8b41-46c8-98af-907fd2c9d37c.webp',
    '450ffd55-fbaf-4334-9db9-c01daf4eca75.webp',
    '31d19848-2e93-4f77-9429-1e39f6abbe6f.webp',
    '8165c69f-0e94-4b41-bbff-11df4829e78f.webp',
    '2bbda2e2-89af-4f7e-b375-5bca9f2d169a.webp',
    'fd70b2ac-e89a-4ce5-8a56-a62458ce4c2c.webp',
    'e7dcd006-1b48-4f36-8d84-3183f1971995.webp',
    '7d893365-2f67-4c48-ab37-d12a8e5c27aa.webp',
    'd1ea7806-a062-4383-b30a-67ef886abedd.webp',
    '1f3d5eda-9da4-4dff-9854-4400ca2be0a5.webp',
    'dd65fd81-4f35-4f55-aa19-017bb97919e0.webp',
  ],
  sql: [
    '4f2bfdb4-d880-4713-8340-08123cd95062.webp',
    '2d91ea29-e253-4af0-83a0-e3174cd15683.webp',
    'c2d543a1-cdce-4d1a-96aa-4c8bae44b0de.webp',
    '07917a00-8f1f-428c-b532-ca1eb99e13d5.webp',
    '36096ccf-7c1a-4a6a-b2e1-6b95cd53a6e4.webp',
    '915a3f9b-d13e-4ea6-8663-c2700ed2944f.webp',
    'ff30c65d-e659-4de5-a748-3386338a659e.webp',
    'ae1cd618-4927-43cf-b937-69308b779c4a.webp',
    'bad36c8d-48b3-409e-ae74-1fafa8e3e9b9.webp',
    '364e55ea-73fa-4ac5-a6d8-7430416e3956.webp',
    'f1ab3b63-7a06-4128-b6d2-ad1dc8a1e993.webp',
    '1d2c2f4f-aea5-43ec-8072-70620049ee69.webp',
    '0d8c6375-cbf4-4de9-a5c8-ab6aa068b56e.webp',
    '003e8c67-7bc2-4dd0-8304-17f19b70cb8e.webp',
    '0a1ed66c-82c3-4074-9c5f-0c9c0cf81885.webp',
  ],
  statistics: [
    '495cdc67-6dcf-4447-b83d-1cf44075a6a0.webp',
    '0c00a625-693d-4011-a20d-7a3111c19aac.webp',
    'cc18a163-9595-4c8e-9776-a7147e2d7170.webp',
    '5f5fa48d-93ac-499a-968a-8554387d4509.webp',
    'ef62138b-5fb4-4475-8f43-b9f2dc676508.webp',
    '36572ad5-d5e9-4372-875c-519379fadd9a.webp',
    'd4b8b6dc-eaa4-4192-b3b4-c5180ed230b6.webp',
    '978db4ba-b6e0-4577-969a-2ff9343aa93d.webp',
    'd62ae045-36c0-4e9c-b934-275f92d97537.webp',
    '0208519a-2352-4e5f-ae44-7269f5ea074a.webp',
    '955387a3-2d22-4528-8b6d-482af3a448d8.webp',
    '1961b588-e6df-4ed1-bdc8-64fc96decd98.webp',
    'ad6ee5d7-1b85-43f0-bc67-e7ba6435d41f.webp',
    'dee60d01-0680-4cb0-8ada-40449ec5f7ab.webp',
  ],
};
